<template>
  <div class="dex-view">
    <h1 class="text-h4 mb-4">QuarnX DEX</h1>
    
    <v-card class="mb-6">
      <v-card-text>
        <div class="text-body-1 mb-2">
          Bem-vindo à Exchange Descentralizada (DEX) da QuarnX. Negocie tokens QRC20 de forma segura e sem intermediários diretamente na blockchain.
        </div>
        <v-alert
          color="warning"
          icon="mdi-information-outline"
          variant="tonal"
          density="compact"
        >
          A DEX está em fase beta e em breve estará disponível para operações reais. Por enquanto, você pode explorar a interface e as funcionalidades.
        </v-alert>
      </v-card-text>
    </v-card>
    
    <v-row>
      <!-- Gráfico de Preço -->
      <v-col cols="12" lg="8">
        <v-card height="400">
          <v-card-title class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-avatar size="28" color="primary" class="mr-2">
                <span class="text-h6 text-white">Q</span>
              </v-avatar>
              
              <v-select
                v-model="parAtual"
                :items="paresTradingDisponiveis"
                variant="plain"
                density="compact"
                hide-details
                class="trading-pair-select"
              ></v-select>
            </div>
            
            <div class="d-flex align-center">
              <v-btn-toggle
                v-model="intervaloTempo"
                mandatory
                density="comfortable"
                variant="outlined"
              >
                <v-btn value="1h">1h</v-btn>
                <v-btn value="1d">1d</v-btn>
                <v-btn value="1w">1w</v-btn>
                <v-btn value="1m">1m</v-btn>
              </v-btn-toggle>
            </div>
          </v-card-title>
          
          <v-divider></v-divider>
          
          <div class="price-chart pa-4 d-flex flex-column justify-center align-center">
            <div class="text-h6 mb-2">{{ parAtual }}</div>
            <div v-if="precoPar > 0" class="text-h4 font-weight-bold mb-3 d-flex align-center">
              {{ precoPar }}
              <span 
                :class="[
                  'ml-2 text-body-2 font-weight-medium',
                  variacaoPositiva ? 'text-success' : 'text-error'
                ]"
              >
                {{ variacaoPositiva ? '+' : '' }}{{ variacaoPar }}%
              </span>
            </div>
            
            <!-- Real market data message instead of simulated candles -->
            <div class="d-flex flex-column align-center justify-center flex-grow-1">
              <v-icon size="64" color="grey-lighten-1" class="mb-3">mdi-chart-line</v-icon>
              <div class="text-body-1 text-grey">Dados de mercado em tempo real</div>
              <div v-if="dexStore.isLoadingPriceHistory" class="mt-2">
                <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
              </div>
              <div v-else-if="dexStore.priceHistory.candles.length === 0" class="text-caption text-grey mt-1">
                Sem dados disponíveis
              </div>
            </div>
            
            <div class="d-flex justify-space-between w-100 mt-2">
              <span class="text-caption">{{ horaInicio }}</span>
              <span class="text-caption">Agora</span>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Formulário de Trading -->
      <v-col cols="12" md="6" lg="4">
        <v-card>
          <v-tabs v-model="tabTrading">
            <v-tab value="comprar">{{ t('dex.buy') }}</v-tab>
            <v-tab value="vender">{{ t('dex.sell') }}</v-tab>
          </v-tabs>
          
          <v-card-text>
            <v-window v-model="tabTrading">
              <v-window-item value="comprar">
                <div class="py-2">
                  <div class="text-body-2 mb-1">Você compra</div>
                  <v-text-field
                    v-model="valorCompra"
                    type="number"
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="coin"
                  ></v-text-field>
                  
                  <div class="text-body-2 mb-1">Você recebe</div>
                  <v-text-field
                    :value="precoPar > 0 ? (valorCompra / precoPar).toFixed(4) : '0'"
                    readonly
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="parAtual.split('/')[0]"
                  ></v-text-field>
                  
                  <div class="text-caption text-grey mb-4">
                    Taxa: 0.3% ({{ (valorCompra * 0.003).toFixed(5) }} {{ coin }})
                  </div>
                  
                  <v-btn
                    color="success"
                    block
                    :disabled="!valorCompra || valorCompra <= 0"
                  >
                    Comprar {{ parAtual.split('/')[0] }}
                  </v-btn>
                </div>
              </v-window-item>
              
              <v-window-item value="vender">
                <div class="py-2">
                  <div class="text-body-2 mb-1">Você vende</div>
                  <v-text-field
                    v-model="valorVenda"
                    type="number"
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="parAtual.split('/')[0]"
                  ></v-text-field>
                  
                  <div class="text-body-2 mb-1">Você recebe</div>
                  <v-text-field
                    :value="(valorVenda * precoPar).toFixed(4)"
                    readonly
                    density="compact"
                    hide-details
                    class="mb-4"
                    :suffix="coin"
                  ></v-text-field>
                  
                  <div class="text-caption text-grey mb-4">
                    Taxa: 0.3% ({{ (valorVenda * precoPar * 0.003).toFixed(5) }} {{ coin }})
                  </div>
                  
                  <v-btn
                    color="error"
                    block
                    :disabled="!valorVenda || valorVenda <= 0"
                  >
                    Vender {{ parAtual.split('/')[0] }}
                  </v-btn>
                </div>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
        
        <!-- Saldos -->
        <v-card class="mt-3">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-wallet-outline</v-icon>
            Seus Saldos
          </v-card-title>
          
          <div v-if="dexStore.tradingPairs.length === 0" class="pa-4 text-center text-grey">
            Sem dados disponíveis
          </div>
          <v-list v-else density="compact">
            <v-list-item>
              <template v-slot:prepend>
                <v-avatar size="24" color="primary">
                  <span class="text-white text-caption">Q</span>
                </v-avatar>
              </template>
              <v-list-item-title>{{ coin }}</v-list-item-title>
              <v-list-item-subtitle>QuarnX</v-list-item-subtitle>
              <template v-slot:append>
                <span class="font-weight-medium">-</span>
              </template>
            </v-list-item>
          </v-list>
          
          <v-card-actions>
            <v-btn variant="text" block color="primary">
              Conectar Carteira
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      
      <!-- Livro de Ordens -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-book-open-variant</v-icon>
            Livro de Ordens
          </v-card-title>
          
          <v-divider></v-divider>
          
          <div v-if="dexStore.isLoadingOrderBook" class="d-flex justify-center align-center" style="height: 300px;">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="dexStore.orderBook.asks.length === 0 && dexStore.orderBook.bids.length === 0" class="d-flex flex-column justify-center align-center" style="height: 300px;">
            <v-icon size="48" color="grey" class="mb-2">mdi-book-open-variant</v-icon>
            <div class="text-body-2 text-grey">Sem dados disponíveis</div>
          </div>
          <div v-else class="order-book pa-2">
            <!-- Ordens de Venda -->
            <div class="sell-orders mb-2">
              <div class="text-caption mb-1 text-grey">Ordens de Venda</div>
              <div 
                v-for="(ask, idx) in dexStore.orderBook.asks.slice(0, 5)" 
                :key="`sell-${idx}`"
                class="order-entry d-flex justify-space-between pa-1"
              >
                <span class="text-error">{{ Number(ask.price).toFixed(5) }}</span>
                <span>{{ Number(ask.amount).toFixed(2) }}</span>
                <span>{{ (Number(ask.price) * Number(ask.amount)).toFixed(2) }}</span>
              </div>
            </div>
            
            <!-- Preço Atual -->
            <div class="current-price text-center py-2 my-2 bg-primary-lighten-5">
              <div class="text-caption text-grey mb-1">Último Preço</div>
              <div class="text-subtitle-1 font-weight-bold">{{ precoPar }}</div>
            </div>
            
            <!-- Ordens de Compra -->
            <div class="buy-orders">
              <div class="text-caption mb-1 text-grey">Ordens de Compra</div>
              <div 
                v-for="(bid, idx) in dexStore.orderBook.bids.slice(0, 5)" 
                :key="`buy-${idx}`"
                class="order-entry d-flex justify-space-between pa-1"
              >
                <span class="text-success">{{ Number(bid.price).toFixed(5) }}</span>
                <span>{{ Number(bid.amount).toFixed(2) }}</span>
                <span>{{ (Number(bid.price) * Number(bid.amount)).toFixed(2) }}</span>
              </div>
            </div>
            
            <div class="d-flex justify-space-between text-caption text-grey mt-2">
              <span>{{ t('dex.price') }}</span>
              <span>Qtd</span>
              <span>{{ t('dex.total') }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Histórico de Transações -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-history</v-icon>
            Negociações Recentes
          </v-card-title>
          
          <v-divider></v-divider>
          
          <div v-if="dexStore.isLoadingTrades" class="d-flex justify-center align-center" style="height: 300px;">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="dexStore.recentTrades.length === 0" class="d-flex flex-column justify-center align-center" style="height: 300px;">
            <v-icon size="48" color="grey" class="mb-2">mdi-history</v-icon>
            <div class="text-body-2 text-grey">Sem dados disponíveis</div>
          </div>
          <div v-else class="trades-history pa-2">
            <div class="d-flex justify-space-between text-caption text-grey mb-1">
              <span>{{ t('dex.price') }}</span>
              <span>{{ t('dex.amount') }}</span>
              <span>Tempo</span>
            </div>
            
            <div 
              v-for="(trade, idx) in dexStore.recentTrades.slice(0, 12)" 
              :key="`trade-${idx}`"
              class="trade-entry d-flex justify-space-between pa-1"
            >
              <span :class="trade.side === 'buy' ? 'text-success' : 'text-error'">
                {{ Number(trade.price || 0).toFixed(5) }}
              </span>
              <span>{{ Number(trade.amount || 0).toFixed(2) }}</span>
              <span class="text-grey">{{ formatTradeTime(trade.timestamp) }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Lista de Pares Disponíveis -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-currency-usd</v-icon>
            Pares de Trading
          </v-card-title>
          
          <v-divider></v-divider>
          
          <v-text-field
            v-model="busca"
            prepend-inner-icon="mdi-magnify"
            label="Buscar par"
            variant="solo-filled"
            density="compact"
            hide-details
            class="pa-2"
            clearable
          ></v-text-field>
          
          <div v-if="dexStore.isLoadingPairs" class="d-flex justify-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="paresFiltrados.length === 0" class="pa-4 text-center text-grey">
            Sem dados disponíveis
          </div>
          <v-list v-else density="compact">
            <v-list-item
              v-for="(par, index) in paresFiltrados"
              :key="index"
              @click="parAtual = par.symbol"
            >
              <template v-slot:prepend>
                <v-avatar size="24" :color="par.color || 'primary'">
                  <span class="text-white text-caption">{{ par.symbol.charAt(0) }}</span>
                </v-avatar>
              </template>
              <v-list-item-title>{{ par.symbol }}</v-list-item-title>
              <v-list-item-subtitle>{{ par.name }}</v-list-item-subtitle>
              <template v-slot:append>
                <div class="text-right">
                  <div :class="(par.change || 0) >= 0 ? 'text-success' : 'text-error'">
                    {{ par.price || '-' }}
                  </div>
                  <div 
                    class="text-caption"
                    :class="(par.change || 0) >= 0 ? 'text-success' : 'text-error'"
                  >
                    {{ (par.change || 0) >= 0 ? '+' : '' }}{{ (par.change || 0).toFixed(2) }}%
                  </div>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'

export default {
  name: 'DexView',
  data() {
    return {
      parAtual: '',
      paresTradingDisponiveis: [],
      intervaloTempo: '1d',
      tabTrading: 'comprar',
      valorCompra: 100,
      valorVenda: 10,
      busca: '',
      paresTrading: []
    }
  },
  computed: {
    dexStore() {
      return useDexStore()
    },
    
    precoPar() {
      const par = this.paresTrading.find(p => p.symbol === this.parAtual)
      return par ? parseFloat(par.price) : 0
    },
    
    variacaoPar() {
      const par = this.paresTrading.find(p => p.symbol === this.parAtual)
      return par ? (par.change || 0).toFixed(2) : '0.00'
    },
    
    variacaoPositiva() {
      return parseFloat(this.variacaoPar) >= 0
    },
    
    horaInicio() {
      const agora = new Date()
      
      if (this.intervaloTempo === '1h') {
        const umHoraAtras = new Date(agora.getTime() - (60 * 60 * 1000))
        return umHoraAtras.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } else if (this.intervaloTempo === '1d') {
        const umDiaAtras = new Date(agora.getTime() - (24 * 60 * 60 * 1000))
        return umDiaAtras.toLocaleDateString()
      } else if (this.intervaloTempo === '1w') {
        const umaSemanaAtras = new Date(agora.getTime() - (7 * 24 * 60 * 60 * 1000))
        return umaSemanaAtras.toLocaleDateString()
      } else if (this.intervaloTempo === '1m') {
        const umMesAtras = new Date(agora.getTime() - (30 * 24 * 60 * 60 * 1000))
        return umMesAtras.toLocaleDateString()
      }
      
      return ''
    },
    
    paresFiltrados() {
      if (!this.busca) return this.paresTrading
      
      const termoBusca = this.busca.toLowerCase()
      return this.paresTrading.filter(par => 
        par.symbol.toLowerCase().includes(termoBusca) || 
        par.name.toLowerCase().includes(termoBusca)
      )
    }
  },
  methods: {
    formatTradeTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMin = Math.floor(diffMs / 60000)
      if (diffMin < 1) return 'agora'
      if (diffMin < 60) return `${diffMin} min`
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    buildPairsFromStore() {
      const c = window.__QUARNX_COIN__ || 'tKinX'
      const storePairs = this.dexStore.tradingPairs

      if (storePairs && storePairs.length > 0) {
        // Use real data from API
        this.paresTrading = storePairs.map(p => {
          const base = p.baseToken?.symbol || p.base || p.name || 'TOKEN'
          const quote = p.quoteToken?.symbol || p.quote || c
          return {
            symbol: `${base}/${quote}`,
            name: p.name || base,
            price: p.lastPrice || p.price || '0',
            change: p.change24h || p.change || 0,
            color: 'primary'
          }
        })
      } else {
        // No data from API — show empty state
        this.paresTrading = []
      }

      this.paresTradingDisponiveis = this.paresTrading.map(p => p.symbol)
      if (this.paresTrading.length > 0 && !this.parAtual) {
        this.parAtual = this.paresTrading[0].symbol
      }
    }
  },
  async mounted() {
    // Load real data from DEX API
    await this.dexStore.initializeData()
    this.buildPairsFromStore()
  }
}
</script>

<style scoped>
.dex-view {
  padding-bottom: 32px;
}

.trading-pair-select {
  max-width: 150px;
}

.order-book, .trades-history {
  height: 340px;
  overflow-y: auto;
}

.order-entry, .trade-entry {
  font-size: 0.85rem;
  border-radius: 2px;
}

.order-entry:hover, .trade-entry:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
</style>
